import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Award, Users, Clock } from 'lucide-react';
import { heroContent, companyInfo } from '../data/mock';

const Home = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src={heroContent.image}
            alt="Luxury Home Renovation"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#484237]/95 via-[#484237]/80 to-transparent" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-2xl">
            <div className="mb-6 inline-block">
              <span className="px-4 py-2 bg-[#D7AC78]/20 backdrop-blur-sm text-[#F8E6C6] text-sm font-medium rounded-full border border-[#D7AC78]/30">
                Excellence in Renovations
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              {heroContent.title}
            </h1>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              {heroContent.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contato"
                className="group inline-flex items-center justify-center px-8 py-4 bg-[#D7AC78] text-white font-medium rounded-lg hover:bg-[#c49a68] transform hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-2xl"
              >
                {heroContent.cta}
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Link>
              <Link
                to="/servicos"
                className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-medium rounded-lg hover:bg-white/20 border border-white/30 transition-all duration-300"
              >
                Our Services
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-white/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#F8E6C6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {companyInfo.stats.map((stat, index) => (
              <div
                key={index}
                className="text-center transform hover:scale-105 transition-transform duration-300"
              >
                <div className="text-4xl md:text-5xl font-bold text-[#484237] mb-2">
                  {stat.number}
                </div>
                <div className="text-sm md:text-base text-[#728481] font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#D7AC78] font-semibold text-sm uppercase tracking-wider mb-4 block">
                About Us
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-[#484237] mb-6 leading-tight">
                Massimo Level
              </h2>
              <p className="text-lg text-[#728481] mb-6 leading-relaxed">
                {companyInfo.description}
              </p>
              <div className="space-y-3 mb-8">
                {companyInfo.values.slice(0, 3).map((value, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="text-[#D7AC78] flex-shrink-0" size={20} />
                    <span className="text-[#484237]">{value}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/sobre"
                className="inline-flex items-center text-[#D7AC78] font-semibold hover:text-[#c49a68] transition-colors group"
              >
                Learn more about us
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Link>
            </div>
            <div className="relative">
              {/* Massimo Logo Display */}
              <div className="rounded-2xl p-12 flex items-center justify-center overflow-hidden" style={{ 
                background: '#484237',
                boxShadow: '0 20px 60px rgba(72, 66, 55, 0.3)',
                minHeight: '500px'
              }}>
                <div className="relative w-full max-w-md">
                  <img
                    src="https://customer-assets.emergentagent.com/job_home-improvement-pro/artifacts/uxclx2q7_Massimo_logo%2010.png"
                    alt="Massimo Symbol"
                    className="w-full h-auto"
                    style={{ 
                      filter: 'drop-shadow(0 15px 35px rgba(215, 172, 120, 0.4))'
                    }}
                  />
                </div>
              </div>
              <div className="absolute -bottom-6 -right-6 bg-[#D7AC78] text-white p-6 rounded-xl shadow-xl">
                <div className="text-3xl font-bold">12+</div>
                <div className="text-sm">Years of Experience</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 bg-[#F8E6C6]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#D7AC78] font-semibold text-sm uppercase tracking-wider mb-4 block">
              Our Services
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#484237] mb-4">
              Complete Renovation Solutions
            </h2>
            <p className="text-lg text-[#728481] max-w-2xl mx-auto">
              We offer specialized services to transform your spaces with quality and design
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {[
              {
                icon: Award,
                title: "Premium Quality",
                description: "Top-tier materials and impeccable finishes"
              },
              {
                icon: Users,
                title: "Specialized Team",
                description: "Qualified and experienced professionals"
              },
              {
                icon: Clock,
                title: "On-Time Delivery",
                description: "Commitment to meeting schedule deadlines"
              }
            ].map((feature, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 text-center"
              >
                <div className="w-16 h-16 bg-[#D7AC78]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="text-[#D7AC78]" size={32} />
                </div>
                <h3 className="text-xl font-bold text-[#484237] mb-3">{feature.title}</h3>
                <p className="text-[#728481]">{feature.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/servicos"
              className="inline-flex items-center px-8 py-4 bg-[#D7AC78] text-white font-medium rounded-lg hover:bg-[#c49a68] transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              View All Services
              <ArrowRight className="ml-2" size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#484237] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Transform Your Space?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Schedule a free consultation and discover how we can bring your dream project to life
          </p>
          <Link
            to="/contato"
            className="inline-flex items-center px-8 py-4 bg-[#D7AC78] text-white font-medium rounded-lg hover:bg-[#c49a68] transform hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-2xl"
          >
            Schedule Free Consultation
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
